/**
 * The gate between a vendor listing a product and a customer seeing it.
 *
 * Same blueprint as the other modules, with one thing peculiar to this one: the queue
 * holds two different jobs. A NEW listing is read as a whole product; an EDIT to something
 * already live is read as a diff, while customers keep seeing the approved version. They
 * are one `approval_status` apart in the database and nothing alike on screen, so the row
 * leads with which of the two it is.
 *
 * The other thing it leads with is the wait. A review queue's only real failure is a
 * submission nobody looked at, and the old screen sorted oldest-first while showing no
 * dates at all — so the item that had been sitting there a week looked exactly like the
 * one filed this morning.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Check,
  ClipboardCheck,
  Clock,
  Copy,
  FilePen,
  Hourglass,
  ImageOff,
  MoreHorizontal,
  PackageSearch,
  Percent,
  PillBottle,
  Sparkles,
  Store,
  X,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { ReasonModal } from '@/features/vendors/components/reason-modal';
import { date_short, humanize } from '@/lib/format';
import { export_product_queue } from '@/features/product-approval/api';
import {
  use_approval_counts,
  use_approve_product,
  use_product_queue,
  use_reject_product,
} from '@/features/product-approval/hooks';
import { use_product_categories } from '@/features/product-categories/hooks';
import { category_label, shared_category_options } from '@/features/product-categories/types';
import {
  APPROVAL_STATUSES,
  KIND_LABEL,
  KIND_TONE,
  QUEUE_SORT_FIELDS,
  VERTICAL_LABEL,
  asks_for,
  days_waiting,
  fee_amount,
  publish_blocked,
  queue_kind,
  wait_tone,
  type ApprovalCounts,
  type ApprovalStatus,
  type QueueFilters,
  type QueueItem,
  type Vertical,
} from '@/features/product-approval/types';

const SEGMENTS = ['queue', 'new', 'edits', 'overdue', 'blocked', 'rejected', 'live'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<QueueFilters>> = {
  // The default and the actual job: never-reviewed listings AND edits to live ones.
  queue: {},
  new: { kind: 'new' },
  edits: { kind: 'edit' },
  // Three days is where a vendor stops waiting and starts emailing support.
  overdue: { waiting_days_gte: 3 },
  /*
   * Not a backlog anybody can work through. Every one of these needs the vendor, or a
   * catalogue photo, before a decision is even possible — so they are pulled out of the
   * queue rather than left to be opened and refused one at a time.
   */
  blocked: { kind: 'new', no_photos: 'true' },
  rejected: { status: 'REJECTED' },
  live: { status: 'APPROVED' },
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  queue: {
    label: 'Needs review',
    title: 'Product approvals',
    blurb: 'Everything waiting on a decision — new listings and edits alike',
  },
  new: {
    label: 'New listings',
    title: 'First reviews',
    blurb: 'Products no customer has ever seen, waiting to be published',
  },
  edits: {
    label: 'Edits',
    title: 'Edits to live products',
    blurb: 'Customers keep seeing the approved version until you decide',
  },
  overdue: {
    label: 'Overdue',
    title: 'Waiting three days or more',
    blurb: 'The vendor has been waiting long enough to chase somebody about it',
  },
  blocked: {
    label: 'No photo',
    title: 'Waiting on a photo',
    blurb: 'These cannot go on sale at all — nothing here shows a shopper a picture',
  },
  rejected: {
    label: 'Sent back',
    title: 'Sent back to the vendor',
    blurb: 'Refused with a reason, and never published',
  },
  live: {
    label: 'Live',
    title: 'Published products',
    blurb: 'Approved and on sale — search here to find one already in the shop',
  },
};

export function ProductApprovalPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'queue';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [vertical, set_vertical] = use_url_state('vertical', enum_param(['FOOD', 'PHARMACY'] as const));
  const [category, set_category] = use_url_state('category', string_param);
  const [status, set_status] = use_url_state('status', enum_param(APPROVAL_STATUSES));
  const [linked, set_linked] = use_url_state('linked', bool_param);
  const [has_own_fee, set_has_own_fee] = use_url_state('has_own_fee', bool_param);
  const [no_photos, set_no_photos] = use_url_state('no_photos', bool_param);
  const [prescription, set_prescription] = use_url_state('prescription', bool_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    // Oldest first, because a queue read newest-first is one where the oldest item is
    // the one nobody ever reaches.
    sort_param(QUEUE_SORT_FIELDS, { by: 'submitted_at', dir: 'asc' }),
  );

  const [rejecting, set_rejecting] = useState<QueueItem | null>(null);
  const [reject_error, set_reject_error] = useState<string | null>(null);

  const reset_page = () => set_page(1);

  const filters: QueueFilters = useMemo(
    () => ({
      ...SEGMENT_FILTERS[segment],
      page,
      page_size,
      q: q || undefined,
      vertical: vertical || undefined,
      category: category || undefined,
      // A hand-picked status beats the segment's.
      ...(status ? { status, kind: undefined } : {}),
      linked: linked || undefined,
      has_own_fee: has_own_fee || undefined,
      no_photos: no_photos || SEGMENT_FILTERS[segment].no_photos,
      prescription: prescription || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [segment, page, page_size, q, vertical, category, status, linked, has_own_fee, no_photos, prescription, from, to],
  );

  const { data, isLoading, isFetching, error, refetch } = use_product_queue(filters, sort);
  const { data: counts } = use_approval_counts();
  /*
   * The category list is the admin's own, not a copy in this file. It used to be a
   * hardcoded array here mirroring a Prisma enum there, so adding a category meant
   * editing both and shipping them together.
   */
  const { data: all_categories } = use_product_categories();
  const category_options = useMemo(
    () => shared_category_options(all_categories),
    [all_categories],
  );

  const approve_m = use_approve_product();
  const reject_m = use_reject_product();

  async function approve(item: QueueItem) {
    try {
      await approve_m.mutateAsync({ vertical: item.vertical, id: item.id });
      notify.success(item.pending_changes ? 'Change approved' : 'Product published', {
        description: item.name,
      });
    } catch (err) {
      notify.error(err, 'Could not approve this product.');
    }
  }

  async function reject(reason: string) {
    if (!rejecting) return;
    set_reject_error(null);
    try {
      await reject_m.mutateAsync({ vertical: rejecting.vertical, id: rejecting.id, reason });
      notify.success('Sent back to the vendor', { description: rejecting.name });
      set_rejecting(null);
    } catch (err) {
      set_reject_error(err instanceof Error ? err.message : 'Could not send this product back.');
    }
  }

  /* --------------------------------- chips --------------------------------- */

  const bool_chip = (
    key: string,
    label: string,
    value: string,
    yes: string,
    no: string,
    clear: () => void,
  ) =>
    value
      ? {
          key,
          label,
          value: value === 'true' ? yes : no,
          advanced: true,
          on_clear: () => {
            clear();
            reset_page();
          },
        }
      : null;

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    vertical && {
      key: 'vertical',
      label: 'Kind',
      value: VERTICAL_LABEL[vertical as Vertical],
      on_clear: () => set_vertical(''),
    },
    category && {
      key: 'category',
      label: 'Category',
      value: category_options.find((o) => o.value === category)?.label ?? category,
      on_clear: () => set_category(''),
    },
    status && {
      key: 'status',
      label: 'Status',
      value: humanize(status),
      advanced: true,
      on_clear: () => set_status(''),
    },
    bool_chip('linked', 'Source', linked, 'From the catalogue', 'The vendor’s own', () =>
      set_linked(''),
    ),
    bool_chip('has_own_fee', 'Fee', has_own_fee, 'Set on the listing', 'Inherited', () =>
      set_has_own_fee(''),
    ),
    segment === 'blocked'
      ? null
      : bool_chip('no_photos', 'Photos', no_photos, 'None at all', 'Has one', () =>
          set_no_photos(''),
        ),
    bool_chip('prescription', 'Prescription', prescription, 'Required', 'Not required', () =>
      set_prescription(''),
    ),
    (from || to) && {
      key: 'date',
      label: 'Submitted',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_vertical('');
    set_category('');
    set_status('');
    set_linked('');
    set_has_own_fee('');
    set_no_photos('');
    set_prescription('');
    set_from('');
    set_to('');
    reset_page();
  }

  const metrics: Metric[] = useMemo(() => build_metrics(counts), [counts]);

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<QueueItem>[]>(
    () => [
      {
        id: 'waiting',
        header: 'Waiting',
        // Not sortable, and deliberately so: the clock is `created_at` for a new listing
        // and `updated_at` for an edit, and no single column sorts both honestly.
        enableSorting: false,
        meta: {
          label: 'Waiting',
          width: '7rem',
          csv: (r) => days_waiting(r),
        },
        cell: ({ row }) => {
          const days = days_waiting(row.original);
          const tone = wait_tone(days);
          return (
            <span className="flex min-w-0 flex-col">
              <span
                className={
                  'whitespace-nowrap font-semibold ' +
                  (tone === 'destructive'
                    ? 'text-destructive'
                    : tone === 'warning'
                      ? 'text-warning'
                      : 'text-foreground')
                }
                data-testid={`waiting-${row.original.id}`}
              >
                {days === 0 ? 'Today' : `${days} ${days === 1 ? 'day' : 'days'}`}
              </span>
              <span className="whitespace-nowrap text-micro text-muted-foreground">
                since {date_short(row.original.waiting_since)}
              </span>
            </span>
          );
        },
      },
      {
        id: 'name',
        header: 'Product',
        meta: { sort_key: 'name', label: 'Product', width: '18rem', csv: (r) => r.name },
        cell: ({ row }) => {
          const item = row.original;
          /*
           * The vendor's photo if they uploaded one, otherwise the CURATED one. A
           * catalogue-linked listing usually has no photos of its own, and a queue of grey
           * placeholder boxes is one a reviewer stops reading carefully.
           */
          const thumb = item.images[0]?.path ?? item.catalog_images?.[0]?.path;
          return (
            <span className="flex min-w-0 items-center gap-2.5">
              {thumb ? (
                <AuthImage
                  path={`${thumb}?variant=thumb`}
                  alt={item.name}
                  className="h-9 w-9 shrink-0 overflow-hidden rounded-control object-cover"
                  data-testid={`queue-image-${item.id}`}
                />
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground">
                  <PackageSearch className="h-4 w-4" />
                </span>
              )}
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-semibold" data-testid={`queue-name-${item.id}`}>
                  {item.name}
                </span>
                <span className="truncate text-micro text-muted-foreground">
                  {[
                    item.brand,
                    category_label(all_categories, item.vertical, item.category),
                    item.pack_size,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: 'asks',
        header: 'What you’re deciding',
        enableSorting: false,
        meta: { label: 'What you’re deciding', width: '14rem', csv: (r) => asks_for(r) },
        cell: ({ row }) => {
          const item = row.original;
          const kind = queue_kind(item);
          return (
            <span className="flex min-w-0 flex-col gap-1">
              <Badge tone={KIND_TONE[kind]} size="sm" dot data-testid={`queue-kind-${item.id}`}>
                {KIND_LABEL[kind]}
              </Badge>
              <span className="truncate text-micro text-muted-foreground" title={asks_for(item)}>
                {asks_for(item)}
              </span>
            </span>
          );
        },
      },
      {
        id: 'vendor',
        header: 'Vendor',
        enableSorting: false,
        meta: { label: 'Vendor', csv: (r) => r.vendor?.business_name ?? '' },
        cell: ({ row }) => {
          const v = row.original.vendor;
          if (!v) return <span className="text-muted-foreground/60">Vendor removed</span>;
          return (
            <span className="flex min-w-0 flex-col">
              <Link
                to={`/vendors/${v.id}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
              >
                {v.business_name ?? 'No business name'}
              </Link>
              <span className="truncate text-micro text-muted-foreground">
                {v.city ?? VERTICAL_LABEL[row.original.vertical]}
              </span>
            </span>
          );
        },
      },
      {
        id: 'signals',
        header: 'Flags',
        enableSorting: false,
        meta: {
          label: 'Signals',
          csv: (r) =>
            [
              publish_blocked(r) && 'No photo',
              r.is_prescription_required && 'Rx',
              r.catalog_product_id && 'Catalogue',
            ]
              .filter(Boolean)
              .join(' '),
        },
        cell: ({ row }) => {
          const item = row.original;
          const empty_sizes = item.stock.filter((s) => !s.is_in_stock).length;
          return (
            <span className="flex flex-wrap items-center gap-1">
              {/*
                Louder than the other flags on purpose: this one is not information about
                the product, it is the reason the row cannot be actioned at all.
              */}
              {publish_blocked(item) && (
                <Badge tone="destructive" size="sm" dot data-testid={`badge-no-photo-${item.id}`}>
                  <ImageOff className="h-3 w-3" /> No photo
                </Badge>
              )}
              {item.is_prescription_required && (
                <Badge tone="destructive" size="sm" data-testid={`badge-rx-${item.id}`}>
                  <PillBottle className="h-3 w-3" /> Rx
                </Badge>
              )}
              {item.catalog_product_id && (
                <Badge tone="info" size="sm" data-testid={`badge-catalogue-${item.id}`}>
                  Catalogue
                </Badge>
              )}
              {/*
                Not a blocker, but the reviewer should know: approving a listing whose only
                size is empty publishes a product nobody can buy.
              */}
              {empty_sizes > 0 && empty_sizes === item.stock.length && (
                <Badge tone="warning" size="sm" data-testid={`badge-empty-${item.id}`}>
                  No stock
                </Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'fee',
        header: 'Fee',
        enableSorting: false,
        meta: {
          label: 'Fee',
          align: 'right',
          csv: (r) => (r.own_fee ? fee_amount(r.own_fee) : 'Inherited'),
        },
        cell: ({ row }) => {
          const fee = row.original.own_fee;
          /*
           * Whether THIS listing carries a hand-set fee, not what the shopper ends up
           * paying — that needs the whole chain resolved per size, which the review page
           * does. What belongs in a queue is "somebody has already made a decision about
           * this one", because that is the thing you want to know before making another.
           */
          return fee ? (
            <Badge tone="warning" size="sm" data-testid={`fee-${row.original.id}`}>
              <Percent className="h-3 w-3" /> {fee_amount(fee)}
            </Badge>
          ) : (
            <span className="text-micro text-muted-foreground">Inherited</span>
          );
        },
      },
      {
        id: 'submitted_at',
        header: 'Submitted',
        meta: {
          sort_key: 'submitted_at',
          label: 'Submitted',
          hidden_by_default: true,
          csv: (r) => r.submitted_at,
        },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-data text-muted-foreground">
            {date_short(row.original.submitted_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right', width: '1%' },
        cell: ({ row }) => {
          const item = row.original;
          const decidable = item.approval_status === 'PENDING' || !!item.pending_changes;
          // Offering Approve here would produce a 422 and nothing else. Send back still
          // works, and is the right action on a product with no picture.
          const can_approve = decidable && !publish_blocked(item);
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Product actions"
                  data-testid={`row-actions-${item.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {decidable && (
                  <>
                    {can_approve && (
                      <RowAction on_select={() => void approve(item)} testid={`approve-${item.id}`}>
                        <Check className="h-4 w-4" />
                        {item.pending_changes ? 'Approve the change' : 'Approve and publish'}
                      </RowAction>
                    )}
                    <RowAction on_select={() => set_rejecting(item)} testid={`reject-${item.id}`}>
                      <X className="h-4 w-4" /> Send back with a reason
                    </RowAction>
                    <DropdownMenuSeparator />
                  </>
                )}
                <RowAction
                  on_select={() => {
                    void navigator.clipboard.writeText(item.id);
                    notify.success('Product ID copied');
                  }}
                  testid={`copy-${item.id}`}
                >
                  <Copy className="h-4 w-4" /> Copy product ID
                </RowAction>
                {item.vendor && (
                  <RowAction
                    on_select={() => window.open(`/vendors/${item.vendor?.id}`, '_self')}
                    testid={`open-vendor-${item.id}`}
                  >
                    <Store className="h-4 w-4" /> Open the vendor
                  </RowAction>
                )}
                {item.catalog_product_id && (
                  <RowAction
                    on_select={() =>
                      window.open(`/catalogue?q=${encodeURIComponent(item.name)}`, '_self')
                    }
                    testid={`open-master-${item.id}`}
                  >
                    <Sparkles className="h-4 w-4" /> Find it in the catalogue
                  </RowAction>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all_categories],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={
        <SavedViews module={`product-approvals:${segment}`} is_filtered={chips.some(Boolean)} />
      }
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            count:
              v === 'queue'
                ? counts?.needs_review
                : v === 'new'
                  ? counts?.awaiting_first_review
                  : v === 'edits'
                    ? counts?.edits_waiting
                    : v === 'overdue'
                      ? counts?.overdue
                      : v === 'blocked'
                        ? counts?.blocked_no_photo
                        : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<QueueItem>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(r) => `${r.vertical}-${r.id}`}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(r) => `/product-approvals/${r.vertical}/${r.id}`}
        storage_key={`product-approvals:${segment}`}
        export_name="product-approvals"
        on_export_all={() => export_product_queue(filters, sort)}
        caption="Products awaiting review"
        test_id="approval-queue"
        /*
         * No bulk selection, on purpose. Every other list here has it, but the failure mode
         * of a review queue is approving without reading, and a "approve 20 selected"
         * button is a tool for exactly that. Each decision opens the product.
         */
        empty={{
          icon: ClipboardCheck,
          title: chips.some(Boolean)
            ? 'No products match these filters'
            : segment === 'queue'
              ? 'Nothing waiting for review'
              : `No ${copy.label.toLowerCase()} products`,
          description: chips.some(Boolean)
            ? 'Try widening the date range or clearing a filter.'
            : segment === 'queue'
              ? 'New listings and edits from vendors land here as they are submitted.'
              : 'Nothing here yet.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <SelectFilter
                  label="Status"
                  value={status}
                  on_change={(v) => {
                    set_status(v as ApprovalStatus);
                    reset_page();
                  }}
                  options={APPROVAL_STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
                  all_label="Any status"
                  test_id="filter-status"
                />
                <BoolFilter
                  label="Source"
                  value={linked}
                  on_change={(v) => {
                    set_linked(v);
                    reset_page();
                  }}
                  true_label="From the catalogue"
                  false_label="The vendor’s own"
                  test_id="filter-linked"
                />
                <BoolFilter
                  label="Fee"
                  value={has_own_fee}
                  on_change={(v) => {
                    set_has_own_fee(v);
                    reset_page();
                  }}
                  true_label="Set on the listing"
                  false_label="Inherited"
                  test_id="filter-own-fee"
                />
                <BoolFilter
                  label="Photos"
                  value={segment === 'blocked' ? 'true' : no_photos}
                  on_change={(v) => {
                    set_no_photos(v);
                    reset_page();
                  }}
                  true_label="None at all"
                  false_label="Has one"
                  test_id="filter-no-photos"
                />
                <BoolFilter
                  label="Prescription"
                  value={prescription}
                  on_change={(v) => {
                    set_prescription(v);
                    reset_page();
                  }}
                  true_label="Required"
                  false_label="Not required"
                  test_id="filter-prescription"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Product, brand or vendor…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Kind"
              value={vertical}
              on_change={(v) => {
                set_vertical(v as Vertical);
                reset_page();
              }}
              options={[
                { value: 'FOOD', label: VERTICAL_LABEL.FOOD },
                { value: 'PHARMACY', label: VERTICAL_LABEL.PHARMACY },
              ]}
              all_label="Both"
              test_id="filter-vertical"
            />
            <SelectFilter
              label="Category"
              value={category}
              on_change={(v) => {
                set_category(v);
                reset_page();
              }}
              options={category_options}
              all_label="Any category"
              test_id="filter-category"
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Submitted"
            />
          </FilterBar>
        }
      />

      <ReasonModal
        open={!!rejecting}
        title="Send this product back"
        description={`The vendor reads this. Say what needs to change about “${rejecting?.name ?? ''}”.`}
        confirm_label="Send back"
        variant="destructive"
        loading={reject_m.isPending}
        error={reject_error}
        on_confirm={reject}
        on_cancel={() => {
          set_rejecting(null);
          set_reject_error(null);
        }}
      />
    </DashboardLayout>
  );
}

function build_metrics(counts: ApprovalCounts | undefined): Metric[] {
  return [
    {
      key: 'needs_review',
      label: 'Needs review',
      value: counts?.needs_review?.toLocaleString('en-IN'),
      hint: 'New listings and edits',
      tone: counts && counts.needs_review > 0 ? 'warning' : 'default',
      to: '/product-approvals?segment=queue',
      icon: ClipboardCheck,
      loading: !counts,
    },
    {
      key: 'blocked',
      label: 'No photo',
      /*
       * Replaces a "never published" tile that only repeated the New listings tab. This
       * number is different in kind: it is the part of the queue nobody can decide on,
       * because a product with no picture cannot go on sale at all.
       */
      value: counts?.blocked_no_photo?.toLocaleString('en-IN'),
      hint: 'Cannot be published as they are',
      tone: counts && counts.blocked_no_photo > 0 ? 'destructive' : 'default',
      to: '/product-approvals?segment=blocked',
      icon: ImageOff,
      loading: !counts,
    },
    {
      key: 'edits',
      label: 'Edits waiting',
      value: counts?.edits_waiting?.toLocaleString('en-IN'),
      hint: 'Live, with a change parked',
      to: '/product-approvals?segment=edits',
      icon: FilePen,
      loading: !counts,
    },
    {
      key: 'overdue',
      label: 'Waiting 3+ days',
      // The number the queue existed to hide: sorted oldest-first with no dates on screen,
      // a submission from last week looked exactly like one filed this morning.
      value: counts?.overdue?.toLocaleString('en-IN'),
      hint: 'Vendors start chasing',
      tone: counts && counts.overdue > 0 ? 'destructive' : 'default',
      to: '/product-approvals?segment=overdue',
      icon: Hourglass,
      loading: !counts,
    },
    {
      key: 'oldest',
      label: 'Oldest wait',
      value: counts
        ? counts.oldest_wait_days === 0
          ? 'Today'
          : `${counts.oldest_wait_days}d`
        : undefined,
      hint: counts?.needs_review ? 'The longest anything has sat' : 'Queue is clear',
      tone: counts && counts.oldest_wait_days >= 7 ? 'destructive' : 'default',
      to: '/product-approvals?segment=overdue',
      icon: Clock,
      loading: !counts,
    },
  ];
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}
